/// <reference types="jwplayer" />
import { Alert } from 'antd';
import { useEffect, useRef, useState } from 'react';

import { waitFor } from '@suntex.core/utils';

import { useEffectAddScriptOnce } from '../../hooks';
import { LoadingSpinner } from '../LoadingSpinner/LoadingSpinner';

const JWPLAYER_SRC = 'https://cdn.jwplayer.com/libraries/fCebANd0.js';

export interface VideoPlayerProps {
  mediaId?: string;
  file?: string;
  /** Called when the video finishes playing. */
  onComplete?: () => void;
  height?: number | string;
  width?: number | string;
}

type PlayerStatus = 'loading' | 'ready' | 'error';

/** Plays a video via JWPlayer, either from a JW-hosted media id or a direct file URL. */
export function VideoPlayer({
  mediaId,
  file,
  onComplete,
  height = 270,
  width = '100%',
}: Readonly<VideoPlayerProps>) {
  const playerRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<PlayerStatus>('loading');
  // Keep the latest onComplete in a ref so JWPlayer's 'complete' listener
  // never calls a stale closure without re-initializing the player.
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffectAddScriptOnce(JWPLAYER_SRC);

  useEffect(() => {
    const el = playerRef.current;
    if (!el) return;

    if (!mediaId && !file) {
      setStatus('error');
      return;
    }

    let cancelled = false;
    setStatus('loading');

    void initJWPlayer(
      el,
      { mediaId, file, height, width },
      () => {
        if (cancelled) return;
        setStatus('ready');
      },
      () => {
        if (cancelled) return;
        setStatus('error');
      },
      () => onCompleteRef.current,
      () => cancelled,
    );

    return () => {
      cancelled = true;
      try {
        jwplayer(el).remove();
      } catch {
        // JW Player can throw if the instance was already destroyed.
      }
    };
  }, [mediaId, file, height, width]);

  if (status === 'error') {
    return (
      <Alert
        type="warning"
        showIcon
        message="Video unavailable"
        description="We couldn't load this video. Please try again later."
      />
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      {status === 'loading' && <LoadingSpinner />}
      <div ref={playerRef} />
    </div>
  );
}

async function initJWPlayer(
  el: HTMLDivElement,
  {
    mediaId,
    file,
    height,
    width,
  }: Pick<VideoPlayerProps, 'mediaId' | 'file' | 'height' | 'width'>,
  onReady: () => void,
  onError: () => void,
  getOnComplete: () => (() => void) | undefined,
  isCancelled: () => boolean,
) {
  // waitFor() resolves false (rather than throwing) on timeout, so bail out
  // and surface an error instead of proceeding with an unloaded jwplayer.
  const loaded = await waitFor(() => typeof jwplayer !== 'undefined');
  if (!loaded) {
    onError();
    return;
  }

  // Bail out if the component was unmounted while we were waiting for
  // jwplayer to load: the effect's cleanup already ran (and no-op'd since
  // no player existed yet), so creating one now would never be removed.
  if (isCancelled()) return;

  jwplayer.key = '2jTSsa3QZDMBgIZdcXHUeQnD8nJtfkSYsnJbw73ZMKhCIWrL';

  const player = jwplayer(el);

  // Attach 'ready' before calling setup(): if JWPlayer is already loaded
  // (e.g. cached script on remount), 'ready' can fire synchronously from
  // within setup() and would otherwise be missed.
  player.on('ready', () => {
    onReady();
  });

  player.setup({
    ...(mediaId
      ? { playlist: `//cdn.jwplayer.com/v2/media/${mediaId}` }
      : { file }),
    aspectratio: '16:9',
    controls: true,
    displaydescription: false,
    displaytitle: false,
    stagevideo: false,
    stretching: 'uniform',
    height,
    width,
  } as unknown as jwplayer.SetupConfig);

  // 'complete' can never fire synchronously (playback always takes time to
  // reach the end), so it's safe -- and, per this player build, necessary --
  // to bind it after setup() rather than before.
  player.on('complete', () => {
    getOnComplete()?.();
  });
}
